import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	ViewRef,
	inject,
	input,
} from "@angular/core";
import { ZoomToolbarIconClasses } from "common/zoom/zoom-toolbar/zoom-toolbar-iconclasses.interface";
import { Step, ZoomOutBehavior, ZoomToolbarService } from "common/zoom/zoom-toolbar/zoom-toolbar.service";
import { Subscription } from "rxjs";

import { TranslateModule } from "@ngx-translate/core";

@Component({
	selector: "stages-zoom-toolbar",
	templateUrl: "./zoom-toolbar.component.html",
	styleUrls: ["./zoom-toolbar.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslateModule],
})
export class ZoomToolbarComponent implements OnInit, OnDestroy {
	private readonly zoomToolbarService = inject(ZoomToolbarService);
	private readonly changeDetector = inject(ChangeDetectorRef);

	// this is a pattern to make the enum available in the template
	get Step(): typeof Step {
		return Step;
	}

	readonly zoomToolbarIconClasses = input<ZoomToolbarIconClasses>({
		header: "ico ico-zoom",
		oneStepDown: "ico ico-minus",
		oneStepUp: "ico ico-plus",
		fitToPage: "ico ico-fit-to-page",
	});

	zoomValue?: number;
	bottomBoundaryReached: boolean = false;
	topBoundaryReached: boolean = false;

	private zoomOutBehavior: ZoomOutBehavior = ZoomOutBehavior.useFitToPageSizeForMaxZoomOut;
	private subscriptions?: Subscription;

	ngOnInit(): void {
		this.subscriptions = this.zoomToolbarService.zoomValue.subscribe({
			next: (value) => {
				this.zoomValue = value;
				this.deactivateStepDownAndFitToPageButtonIfNecessary();
				this.deactivateStepUpButtonIfNecessary();
				if (!(this.changeDetector as ViewRef).destroyed) {
					this.changeDetector.detectChanges();
				}
			},
		});
		this.subscriptions.add(
			this.zoomToolbarService.zoomOutBehavior.subscribe({
				next: (value) => {
					this.zoomOutBehavior = value;
					this.deactivateStepDownAndFitToPageButtonIfNecessary();
					if (!(this.changeDetector as ViewRef).destroyed) {
						this.changeDetector.detectChanges();
					}
				},
			}),
		);
	}

	private deactivateStepUpButtonIfNecessary(): void {
		if (this.zoomValue !== undefined) {
			this.topBoundaryReached = this.zoomValue >= this.zoomToolbarService.ZOOM_MAX_ZOOM_IN_VALUE;
		}
	}

	private deactivateStepDownAndFitToPageButtonIfNecessary(): void {
		if (this.zoomValue !== undefined) {
			this.bottomBoundaryReached =
				this.zoomOutBehavior === ZoomOutBehavior.useFitToPageSizeForMaxZoomOut
					? this.zoomValue <= this.zoomToolbarService.ZOOM_MAX_ZOOM_OUT_VALUE
					: this.zoomValue <= this.zoomToolbarService.ZOOM_ORIGINAL_IMAGE_SIZE_VALUE;
		}
	}

	fitToPageButtonClicked(): void {
		this.zoomToolbarService.setZoomValueToLowestPossibleValue();
	}

	stepButtonClicked(step: Step): void {
		this.zoomToolbarService.performStep(step);
	}

	ngOnDestroy(): void {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}
}
